import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Layout } from '../components/layout';
import { KeyRound, Mail, ArrowLeft } from 'lucide-react';
import api from '../utils/api';

const ForgotPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/auth/forgot-password', { email });
      setEmailSent(true);
      toast.success('If that email exists, a reset link has been sent.');
    } catch (error) {
      toast.error('Failed to send reset email. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/reset-password', {
        token,
        new_password: newPassword,
      });
      toast.success('Password reset successful! You can now log in.');
      navigate('/login');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to reset password';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // If we have a token, show the reset form
  if (token) {
    return (
      <Layout showFooter={false}>
        <div className="min-h-[calc(100vh-64px)] flex items-center justify-center p-4">
          <div className="max-w-md w-full">
            <div className="text-center mb-8">
              <h1 className="font-headline text-4xl text-white mb-2">
                RESET PASSWORD
              </h1>
              <p className="font-mono text-xs text-gray-500 tracking-[0.2em] uppercase">
                Enter your new password
              </p>
            </div>

            <div className="bg-[#111] border border-[#1a1a1a] p-8 rounded">
              <form onSubmit={handleResetPassword} className="space-y-6">
                <div>
                  <label htmlFor="newPassword" className="block font-mono text-xs text-[#C5A52A] mb-2 uppercase tracking-wider">
                    New Password
                  </label>
                  <input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength="6"
                    className="w-full px-4 py-3 bg-black border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none transition-colors"
                    placeholder="••••••••"
                  />
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block font-mono text-xs text-[#C5A52A] mb-2 uppercase tracking-wider">
                    Confirm Password
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength="6"
                    className="w-full px-4 py-3 bg-black border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none transition-colors"
                    placeholder="••••••••"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#C5A52A] hover:bg-[#D4B93A] disabled:bg-[#C5A52A]/50 text-black font-display text-sm py-3 tracking-wider transition-colors flex items-center justify-center gap-2"
                >
                  <KeyRound className="w-4 h-4" />
                  {loading ? 'RESETTING...' : 'RESET PASSWORD'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Otherwise show the request form
  return (
    <Layout showFooter={false}>
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <h1 className="font-headline text-4xl text-white mb-2">
              FORGOT PASSWORD
            </h1>
            <p className="font-mono text-xs text-gray-500 tracking-[0.2em] uppercase">
              Request a password reset
            </p>
          </div>

          <div className="bg-[#111] border border-[#1a1a1a] p-8 rounded">
            {emailSent ? (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-green-900/30 rounded-full flex items-center justify-center mx-auto">
                  <Mail className="w-8 h-8 text-green-500" />
                </div>
                <h2 className="font-display text-lg text-white">Check Your Email</h2>
                <p className="text-gray-400 font-mono text-sm">
                  If an account exists with that email, you'll receive a password reset link shortly.
                </p>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 text-[#C5A52A] hover:text-[#D4B93A] font-mono text-sm transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Login
                </Link>
              </div>
            ) : (
              <form onSubmit={handleRequestReset} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block font-mono text-xs text-[#C5A52A] mb-2 uppercase tracking-wider">
                    Email Address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full px-4 py-3 bg-black border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none transition-colors"
                    placeholder="your@email.com"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#C5A52A] hover:bg-[#D4B93A] disabled:bg-[#C5A52A]/50 text-black font-display text-sm py-3 tracking-wider transition-colors flex items-center justify-center gap-2"
                >
                  <Mail className="w-4 h-4" />
                  {loading ? 'SENDING...' : 'SEND RESET LINK'}
                </button>

                <div className="pt-4 border-t border-[#1a1a1a] text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-2 text-gray-400 hover:text-white font-mono text-xs transition-colors"
                  >
                    <ArrowLeft className="w-3 h-3" />
                    Back to Login
                  </Link>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ForgotPassword;
